import { useContext } from "react";
import { useHistory } from "react-router-dom";
import { useResetRecoilState, useSetRecoilState } from "recoil";
import ApolloClientContext from "oc/context/apollo-client-context";
import { useOcCurrentUserState } from "oc/hooks";
import {
  ocCurrentUserState,
  ocCustomerLicencesState,
  ocIsTemporaryPasswordState,
  ocNarrowMenuState,
  ocRefreshTokenState,
  ocRefreshTokenTimestampState,
  ocSessionPeriodState,
  twoFaVerificationPendingState,
} from "oc/states";
import { ocActiveMenuItemState } from "oc/states/active-menu-item-state";
import { ocActiveMenuState } from "oc/states/active-menu-state";

export const useOcLoginState = () => {
  const { clearCache } = useContext(ApolloClientContext);
  const { emailAddress, setData: setocCurrentUserState } =
    useOcCurrentUserState();
  const setTimestamp = useSetRecoilState(ocRefreshTokenTimestampState);
  const setSessionPeriod = useSetRecoilState(ocSessionPeriodState);
  const setRefreshTokenTimestamp = useSetRecoilState(
    ocRefreshTokenTimestampState,
  );
  const setIsTemporaryPassword = useSetRecoilState(ocIsTemporaryPasswordState);

  const history = useHistory();

  interface OnLoginParams {
    accessToken: string;
    currentUser: {
      employee: string;
      email: string;
      ldapUser: boolean;
    };
    sessionPeriod: number;
    accessTokenPeriod: number;
    isTemporaryPassword: boolean;
    contractCode: string;
  }

  const onLogin = async ({
    accessToken,
    currentUser,
    sessionPeriod,
    accessTokenPeriod,
    isTemporaryPassword,
    contractCode,
  }: OnLoginParams) => {
    setSessionPeriod(sessionPeriod);
    setIsTemporaryPassword(isTemporaryPassword);
    let now = new Date().toUTCString();
    setTimestamp(now);
    setRefreshTokenTimestamp(now);
    setocCurrentUserState({
      employeeCode: currentUser.employee,
      contractCode: contractCode,
      emailAddress: currentUser.email,
      ldapUser: currentUser.ldapUser,
      accessTokenPeriod: accessTokenPeriod,
      accessToken: accessToken,
    });
  };

  const resetocCurrentUserState = useResetRecoilState(ocCurrentUserState);
  const resetRefreshTokenTimestamp = useResetRecoilState(
    ocRefreshTokenTimestampState,
  );
  const resetocNarrowMenuState = useResetRecoilState(ocNarrowMenuState);
  const resetRefreshTokenErrorState = useResetRecoilState(ocRefreshTokenState);
  const resetActiveMenuState = useResetRecoilState(ocActiveMenuState);
  const resetActiveMenuItemState = useResetRecoilState(ocActiveMenuItemState);
  const resetTwoFaVerificationPending = useResetRecoilState(twoFaVerificationPendingState);
  const resetCustomerLicences = useResetRecoilState(ocCustomerLicencesState);

  const onLogout = async () => {
    resetRefreshTokenTimestamp();
    resetocCurrentUserState();
    resetocNarrowMenuState();
    resetRefreshTokenErrorState();
    resetActiveMenuState();
    resetActiveMenuItemState();
    resetTwoFaVerificationPending();
    resetCustomerLicences();
    if (clearCache) {
      await clearCache();
    }

    return history.push("/");
  };

  return { isLoggedIn: emailAddress ? true : false, onLogin, onLogout };
};

export default useOcLoginState;
